import { defineComponent as _defineComponent } from 'vue'
import { i18n } from '@/plugins/vue-i18n';
import SelectStatus from '@/components/SelectStatus.vue';
import SelectTags from '@/components/SelectTags.vue';
import SelectInTextField from '@/components/SelectInTextField.vue';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import { DepartmentTreeNode } from '@/interfaces/model/general/department';
import {
  UseSdsFilter,
  SdsesFilterParamsKeys,
  SdsesFilterParamsValue,
  WORKING_ENV_RECOMMENDATION,
  RISK_LEVELS,
  getRiskLevelChipColor,
  getWorkingEnvRecommendationChipColor,
} from '@/composables/useSdsFilter';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { computed, ref, UnwrapRef } from 'vue';
import sharedMessages from '@/lib/shared-messages';
import { NO_DEPARTMENT_ID } from '@/lib/constant';
import { StateItem } from '@/interfaces/lib/model';
import { readUserLocale } from '@/store/main/getters';
import { store } from '@/store/index';
import { formatDatePickerDay } from '@/utils/datetime';

type Props = {
  onApplyFilter: () => void;
  onClose: () => void;
  isOpen: boolean;
  formFilterParams: UnwrapRef<UseSdsFilter['formFilterParams']>;
  statusOptions: UnwrapRef<UseSdsFilter['statusOptions']>;
  userOptions: UnwrapRef<UseSdsFilter['userOptions']>;
  tagOptions: UnwrapRef<UseSdsFilter['tagOptions']>;
  departmentOptions: UnwrapRef<UseSdsFilter['departmentOptions']>;
  departmentTree: DepartmentTreeNode[];
  dynamicListOptions: UnwrapRef<UseSdsFilter['dynamicListOptions']>;
};

type FilterKey = Extract<
  SdsesFilterParamsKeys,
  'statusGroupId' | 'assigneeUsers' | 'departmentUuids' | 'dynamicListIds'
>;


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsFilterForm',
  props: {
    onApplyFilter: null,
    onClose: null,
    isOpen: { type: Boolean },
    formFilterParams: null,
    statusOptions: null,
    userOptions: null,
    tagOptions: null,
    departmentOptions: null,
    departmentTree: null,
    dynamicListOptions: null
  },
  emits: ["update"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    e: 'update',
    key: SdsesFilterParamsKeys,
    value: SdsesFilterParamsValue
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  onApplyFilter: () => void;
  onClose: () => void;
  isOpen: boolean;
  formFilterParams: UnwrapRef<UseSdsFilter['formFilterParams']>;
  statusOptions: UnwrapRef<UseSdsFilter['statusOptions']>;
  userOptions: UnwrapRef<UseSdsFilter['userOptions']>;
  tagOptions: UnwrapRef<UseSdsFilter['tagOptions']>;
  departmentOptions: UnwrapRef<UseSdsFilter['departmentOptions']>;
  departmentTree: DepartmentTreeNode[];
  dynamicListOptions: UnwrapRef<UseSdsFilter['dynamicListOptions']>;
};

const locale = computed(() => readUserLocale(store));

const $t = i18n.t.bind(i18n);





const { isFeatureActive, COMPANY_FEATURES } = useCompanyFeatureComposable();

const isRiskAssessmentFilterActive = computed(() =>
  isFeatureActive(COMPANY_FEATURES.CHEMICAN_RISK_ASSESSMENT)
);

const workingEnvRecommendationOptions = computed<StateItem[]>(() => [
  {
    value: WORKING_ENV_RECOMMENDATION.RECOMMENDED,
    title: String($t(sharedMessages.riskAssessmentWorkingEnvInputRecommended)),
    color: getWorkingEnvRecommendationChipColor(
      WORKING_ENV_RECOMMENDATION.RECOMMENDED
    ),
  },
  {
    value: WORKING_ENV_RECOMMENDATION.COMPLETED,
    title: String($t(sharedMessages.riskAssessmentWorkingEnvInputCompleted)),
    color: getWorkingEnvRecommendationChipColor(
      WORKING_ENV_RECOMMENDATION.COMPLETED
    ),
  },
  {
    value: WORKING_ENV_RECOMMENDATION.NOT_RECOMMENDED,
    title: String(
      $t(sharedMessages.riskAssessmentWorkingEnvInputNotRecommendedFilterOption)
    ),
    color: getWorkingEnvRecommendationChipColor(
      WORKING_ENV_RECOMMENDATION.NOT_RECOMMENDED
    ),
  },
]);

const riskLevelOptions = computed<StateItem[]>(() =>
  RISK_LEVELS.map((level) => ({
    value: level,
    title: String($t(sharedMessages.riskAssessmentLevel, { level })),
    color: getRiskLevelChipColor(level),
  }))
);

const isOpenRevisedAtStartDatePicker = ref<boolean>(false);
const isOpenRevisedAtEndDatePicker = ref<boolean>(false);
const isOpenCreatedAtStartDatePicker = ref<boolean>(false);
const isOpenCreatedAtEndDatePicker = ref<boolean>(false);

const handleCloseDatePicker = () => {
  isOpenRevisedAtStartDatePicker.value = false;
  isOpenRevisedAtEndDatePicker.value = false;
  isOpenCreatedAtStartDatePicker.value = false;
  isOpenCreatedAtEndDatePicker.value = false;
};

const handleChangeInput = (
  key: SdsesFilterParamsKeys,
  value: SdsesFilterParamsValue
) => {
  emits('update', key, value);
};

const handleChangeDepartmentUuids = (value: string[]) => {
  const includeNoDepartment =
    props.formFilterParams.departmentUuids.includes(NO_DEPARTMENT_ID);
  handleChangeInput(
    'departmentUuids',
    includeNoDepartment ? [NO_DEPARTMENT_ID, ...value] : value
  );
};

const handleChangeIncludeNoDepartment = (value: boolean) => {
  const currentUuids = props.formFilterParams.departmentUuids.filter(
    (id) => id !== NO_DEPARTMENT_ID
  );
  handleChangeInput(
    'departmentUuids',
    value ? [NO_DEPARTMENT_ID, ...currentUuids] : currentUuids
  );
};

const currentFilterStatus = computed(() =>
  props.statusOptions
    .filter((s) => props.formFilterParams.statusGroupId.includes(s.value))
    .filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    )
);

const currentFilteredUsers = computed(() =>
  props.userOptions.filter((u) =>
    props.formFilterParams.assigneeUsers.includes(u.value)
  )
);

const currentFilteredDepartments = computed(() =>
  props.departmentOptions.filter((d) =>
    props.formFilterParams.departmentUuids.includes(d.value)
  )
);

const currentFilteredDynamicLists = computed(() =>
  props.dynamicListOptions.filter((c) =>
    props.formFilterParams.dynamicListIds.includes(c.value)
  )
);

const filterType = (key: FilterKey) => {
  switch (key) {
    case 'statusGroupId':
      return currentFilterStatus.value;
    case 'assigneeUsers':
      return currentFilteredUsers.value;
    case 'departmentUuids':
      return currentFilteredDepartments.value;
    case 'dynamicListIds':
      return currentFilteredDynamicLists.value;
    default:
      return [];
  }
};

const removeItem = (key: FilterKey, value: SdsesFilterParamsValue) => {
  const filterValue = filterType(key);
  if (!filterValue.length) {
    return;
  }
  handleChangeInput(
    key,
    filterValue.filter((item) => item.value !== value).map((item) => item.value)
  );
};

return { __sfc: true,locale, $t, props, emits, isFeatureActive, COMPANY_FEATURES, isRiskAssessmentFilterActive, workingEnvRecommendationOptions, riskLevelOptions, isOpenRevisedAtStartDatePicker, isOpenRevisedAtEndDatePicker, isOpenCreatedAtStartDatePicker, isOpenCreatedAtEndDatePicker, handleCloseDatePicker, handleChangeInput, handleChangeDepartmentUuids, handleChangeIncludeNoDepartment, currentFilterStatus, currentFilteredUsers, currentFilteredDepartments, currentFilteredDynamicLists, filterType, removeItem, SelectStatus, SelectTags, SelectInTextField, DepartmentTreeSelect, sharedMessages, NO_DEPARTMENT_ID, formatDatePickerDay }
}

})